import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "../../styles/AddressForm.css";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CUSTOMER_FAMILY_MEMBERS } from "../../component/common/Routes/CustomerRoutes";
import ControlledRadioButtonsGroup from "../../component/ui/FormFields/ControlledRadioButtonsGroup";
import {
  fetchFamilyMemberRequest,
  resetFamilyMember,
  updateFamilyMemberRequest,
} from "../../redux/FamilyMembers/actions";
import ControlledSelectField from "../../component/ui/FormFields/ControlledSelectField";
import { selectCountryRequest } from "../../redux/Common/selectorAction";
import { convertDateFormat, formatDatetimeWithAMPM } from "../../Style";

export default function EditFamilyMember() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
    trigger,
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      dob: "",
      passport_number: "",
      aadhar_number: "",
      nationality: "",
      country_code: "",
      phone: "",
      type: "",
      gender_type: "",
    },
  });
  const formValue = watch();
  const { id } = useParams();
  const loading = useSelector((state) => state.familyMember.loading);
  const error = useSelector((state) => state.familyMember.error);
  const success = useSelector((state) => state.familyMember.success);
  const familyMember = useSelector((state) => {
    return state.familyMember?.currentFamilyMember?.data;
  });

  const countries = useSelector((state) => state.selector?.country?.data);
  const countryForCode = countries?.map((value) => {
    return {
      label: ` +${value.phonecode} - ${value.short_name}`,
      value: value.phonecode,
    };
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchFamilyMemberRequest(id));
    }
    return () => {
      dispatch(resetFamilyMember());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (familyMember && Object.keys(familyMember).length > 0) {
      Object.keys(familyMember).forEach((key) => {
        if (key !== "country_code" && key !== "type") {
          setValue(key, familyMember[key]);
        }
      });

      if (familyMember.country_code) {
        const code = familyMember.country_code.replace("+", "");
        setValue("country_code", {
          value: code,
          label: ` +${code} - ${
            countries?.find((c) => c.phonecode === code)?.short_name || ""
          }`,
        });
      }

      if (familyMember.type !== undefined && familyMember.type !== null) {
        setValue("type", String(familyMember.type));
      }

      if (familyMember.dob) {
        setValue("dob", formatDatetimeWithAMPM(familyMember.dob));
      }
    }
  }, [familyMember, setValue, countries]);

  useEffect(() => {
    dispatch(selectCountryRequest());
    dispatch(resetFamilyMember());
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
      });
    }

    if (success) {
      toast.success("Family member updated successfully!", {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
      });

      const timer = setTimeout(() => {
        navigate(CUSTOMER_FAMILY_MEMBERS);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [error, success, navigate]);
  const passportNumber = watch("passport_number");
  const aadharNumber = watch("aadhar_number");
  // useEffect(() => {
  //   if (passportNumber || aadharNumber) {
  //     clearErrors(["passport_number", "aadhar_number"]);
  //   }
  // }, [passportNumber, aadharNumber, clearErrors, trigger]);
  // const validateIdentityDocument = () => {
  //   return (
  //     (passportNumber && passportNumber.trim() !== "") ||
  //     (aadharNumber && aadharNumber.trim() !== "") ||
  //     "Either Passport Number or Aadhar Number is required"
  //   );
  // };
  const passengerDetails = [
    {
      name: "first_name",
      label: "First Name *",
      placeholder: "Enter first name",
      rules: { required: "First Name is required" },
      xs: 12,
      sm: 6,
    },
    {
      name: "last_name",
      label: "Last Name *",
      placeholder: "Enter last name",
      rules: { required: "Last Name is required" },
      xs: 12,
      sm: 6,
    },
    {
      name: "dob",
      label: "Date of Birth *",
      placeholder: "Select date of birth",
      type: "dob",
      rules: { required: "Date of Birth is required" },
      xs: 12,
      sm: 6,
    },
    {
      name: "passport_number",
      label: "Passport Number",
      placeholder: "Enter Passport Number",
      rules: {
        validate: {
          // identityRequired: validateIdentityDocument,
          passportFormat: (value) => {
            if (!value || value.trim() === "") return true;

            const patterns = {
              US: /^[0-9]{9}$/,
              UK: /^[0-9]{9}$|^[A-Z]{2}[0-9]{7}$/,
              EU: /^[A-Z]{1,2}[0-9]{5,7}$/,
              CA: /^[A-Z]{2}[0-9]{6}$/,
              AU: /^[A-Z][0-9]{8}$/,
              IN: /^[A-Z][0-9]{7}$/,
              general: /^[A-Z0-9]{5,12}$/i,
            };

            return (
              patterns.US.test(value) ||
              patterns.UK.test(value) ||
              patterns.EU.test(value) ||
              patterns.CA.test(value) ||
              patterns.AU.test(value) ||
              patterns.IN.test(value) ||
              patterns.general.test(value) ||
              "Please enter a valid passport number"
            );
          },
        },
      },
      xs: 12,
      sm: 6,
    },
    {
      name: "aadhar_number",
      label: "Aadhar Number",
      placeholder: "Enter Aadhar Number",
      type: "Number",
      rules: {
        validate: {
          // identityRequired: validateIdentityDocument,
          aadharFormat: (value) => {
            if (!value || value.trim() === "") return true;

            return (
              /^\d{12}$/.test(value) ||
              "Aadhar Number must be exactly 12 digits"
            );
          },
        },
      },
      xs: 12,
      sm: 6,
    },
    {
      name: "nationality",
      label: "Nationality *",
      placeholder: "Enter Nationality",
      type: "text",
      rules: {
        required: "Nationality is required",
        pattern: {
          value: /^[A-Za-z\s]+$/,
          message: "Only alphabets are allowed",
        },
      },
      xs: 12,
      sm: 6,
      inputProps: {
        onInput: (e) => {
          e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        },
      },
    },
    {
      name: "country_code",
      label: "Country Code *",
      placeholder: "Enter Country Code",
      options: countryForCode,
      rules: { required: "Country Code is required" },
      xs: 2,
      sm: 2,
    },
    {
      name: "phone",
      label: "Mobile No *",
      placeholder: "Enter mobile number",
      type: "Number",
      rules: {
        required: "Mobile No is required",
        pattern: {
          value: /^\d+$/,
          message: "Enter only numeric digits",
        },
      },
      xs: 4,
      sm: 4,
    },
    // {
    //   name: 'type',
    //   label: 'Person Type *',
    //   type: 'select',
    //   options: [
    //     { value: '1', label: 'Infant' },
    //     { value: '2', label: 'Child' },
    //     { value: '3', label: 'Adult' },
    //   ],
    //   rules: { required: 'Select a type' },
    //   xs: 12,
    //   sm: 6,
    // },
    {
      name: "gender_type",
      label: "Gender *",
      type: "radio",
      options: [
        { value: "M", label: "Male" },
        { value: "F", label: "Female" },
        { value: "O", label: "Others" },
      ],
      rules: { required: "Select Gender" },
      xs: 12,
      sm: 6,
    },
  ];

  const onSubmit = (data) => {
    // if (formValue?.aadhar_number === "" && formValue?.passport_number === "") {
    //   setError("aadhar_number", {
    //     type: "server",
    //     message: "Aadhar or Passport Number is needed",
    //   });
    //   setError("passport_number", {
    //     type: "server",
    //     message: "Aadhar or Passport Number is needed",
    //   });
    //   return;
    // }
    const formData = {
      id: id,
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      gender_type: data.gender_type,
      dob: convertDateFormat(data.dob),
      passport_number: data.passport_number,
      aadhar_number: data.aadhar_number,
      nationality: data.nationality,
      country_code: data.country_code?.value
        ? `+${data.country_code.value}`
        : data.country_code,
    };

    dispatch(updateFamilyMemberRequest(formData));
  };
  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card className="form-card">
          <CardContent>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              className="form-heading"
            >
              Edit Passenger Detail
            </Typography>
            <Divider className="form-divider" />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {passengerDetails.map((field, index) => (
                  <Grid item xs={field.xs} sm={field.sm} key={index}>
                    {field.type === "radio" ? (
                      <ControlledRadioButtonsGroup
                        name={field.name}
                        control={control}
                        label={field.label}
                        rules={field.rules}
                        options={field.options || field.radioOptions}
                        errors={errors}
                      />
                    ) : field.type === "select" ? (
                      <ControlledSelectField
                        name={field.name}
                        control={control}
                        label={field.label}
                        rules={field.rules}
                        options={field.options}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                      />
                    ) : (
                      <ControlledTextInputField
                        name={field.name}
                        control={control}
                        label={field.label}
                        placeholder={field.placeholder}
                        rules={field.rules}
                        options={field.options}
                        type={field.type}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box className="button-container">
                  <CustomButton
                    variant="contained"
                    className="cancel-button"
                    type="button"
                    text={"Cancel"}
                    onClick={() => navigate(CUSTOMER_FAMILY_MEMBERS)}
                  />
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    type="submit"
                    text={"Submit"}
                    loading={loading}
                  />
                </Box>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
